import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { aiService } from "@/lib/ai-service";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Badge,
	Progress,
	Tooltip,
	TooltipTrigger,
	TooltipContent,
} from "@/components/primitives";
import {
	BarChart3,
	TrendingUp,
	TrendingDown,
	Calendar,
	Clock,
	Target,
	AlertTriangle,
	CheckCircle,
	Brain,
	Activity,
	Info,
	Users,
	Zap,
	Flag,
	ArrowRight,
	Timer,
	Award,
	Lightbulb,
} from "lucide-react";
import {
	LineChart,
	Line,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	ReferenceLine,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	Legend,
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
} from "recharts";
import { TeamMoodOverview } from "@/features/terms/TeamMoodOverview";
import { GroupAnalysisSection } from "@/components/GroupAnalysisSection";

interface StructuredTeamAnalysisProps {
	topic: any;
	groupId?: string;
	topicId: string;
	users: any[];
	groupInsights?: any;
	dailyTrends?: any[];
	insightsAnalysis?: any;
	weeklyStats?: any;
	onGenerateInsights: () => void;
	insightsLoading: boolean;
}

export function StructuredTeamAnalysis({
	topic,
	groupId,
	topicId,
	users,
	groupInsights,
	dailyTrends = [],
	insightsAnalysis,
	weeklyStats,
	onGenerateInsights,
	insightsLoading,
}: StructuredTeamAnalysisProps) {
	const [activeTab, setActiveTab] = useState("analysis");

	const getSentimentColor = (sentiment: string) => {
		switch (sentiment) {
			case "positive":
				return "bg-green-100 text-green-800";
			case "negative":
				return "bg-red-100 text-red-800";
			case "neutral":
				return "bg-gray-100 text-gray-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const getHealthStatusColor = (status: string) => {
		switch (status) {
			case "healthy":
				return "text-green-600";
			case "concerning":
				return "text-yellow-600";
			case "critical":
				return "text-red-600";
			default:
				return "text-gray-600";
		}
	};

	const tabs = [
		{ id: "analysis", label: "Group Analysis", icon: Brain },
		{
			id: "patterns",
			label: "Behavioral Insights & Patterns",
			icon: BarChart3,
		},
		{ id: "roadmap", label: "Coaching Roadmap & Interventions", icon: Target },
	];

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center space-x-2">
					<BarChart3 className="w-5 h-5" />
					<span>Team Analysis & Insights</span>
				</CardTitle>
				<CardDescription>
					Structured analysis and actionable intelligence for team development
				</CardDescription>
			</CardHeader>
			<CardContent>
				<div className="space-y-6">
					{/* Group Analysis Report */}
					<GroupAnalysisSection
						group={{
							id: `topic-${topicId}`,
							name: `${topic?.title || "Topic"} Coaching Insights`,
							description: `Detailed AI analysis for: ${topic?.description || "this topic"}`,
							member_count: users.length,
							members: users.map((user) => ({
								id: `member-${user.id}`,
								mentee_id: user.id,
								mentee_name: user.name,
								mentee_email: user.email,
								created_at: user.last_activity,
							})),
						}}
						topicId={topicId}
					/>

					{/* Coaching Roadmap & Interventions */}
					<div className="space-y-6">
						{/* Priority Matrix - Dynamic from insightsAnalysis */}
						{insightsAnalysis?.recommendations &&
							insightsAnalysis.recommendations.length > 0 && (
								<div className="p-4 border rounded-lg">
									<h4 className="font-medium mb-4 flex items-center">
										<Target className="w-4 h-4 mr-2" />
										Priority Matrix & Interventions
									</h4>

									<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
										{/* High Priority */}
										{insightsAnalysis.recommendations.filter(
											(rec: any) => rec.priority === "high",
										).length > 0 && (
											<div className="border-l-4 border-red-500 pl-4">
												<h5 className="font-medium text-red-700 mb-3 flex items-center">
													<AlertTriangle className="w-4 h-4 mr-1" />
													High Priority (Immediate)
												</h5>
												<div className="space-y-2">
													{insightsAnalysis.recommendations
														.filter((rec: any) => rec.priority === "high")
														.slice(0, 3)
														.map((rec: any, idx: number) => (
															<div
																key={idx}
																className="p-2 bg-red-50 rounded text-sm"
															>
																<div className="font-medium">{rec.action}</div>
																<div className="text-xs text-muted-foreground mt-1">
																	{rec.reasoning}
																</div>
																<div className="text-xs text-red-600 mt-1">
																	Timeframe: {rec.timeframe}
																</div>
															</div>
														))}
												</div>
											</div>
										)}

										{/* Medium Priority */}
										{insightsAnalysis.recommendations.filter(
											(rec: any) => rec.priority === "medium",
										).length > 0 && (
											<div className="border-l-4 border-yellow-500 pl-4">
												<h5 className="font-medium text-yellow-700 mb-3 flex items-center">
													<Clock className="w-4 h-4 mr-1" />
													Medium Priority (2-4 weeks)
												</h5>
												<div className="space-y-2">
													{insightsAnalysis.recommendations
														.filter((rec: any) => rec.priority === "medium")
														.slice(0, 3)
														.map((rec: any, idx: number) => (
															<div
																key={idx}
																className="p-2 bg-yellow-50 rounded text-sm"
															>
																<div className="font-medium">{rec.action}</div>
																<div className="text-xs text-muted-foreground mt-1">
																	{rec.reasoning}
																</div>
																<div className="text-xs text-yellow-600 mt-1">
																	Timeframe: {rec.timeframe}
																</div>
															</div>
														))}
												</div>
											</div>
										)}

										{/* Low Priority */}
										{insightsAnalysis.recommendations.filter(
											(rec: any) => rec.priority === "low",
										).length > 0 && (
											<div className="border-l-4 border-blue-500 pl-4">
												<h5 className="font-medium text-blue-700 mb-3 flex items-center">
													<Lightbulb className="w-4 h-4 mr-1" />
													Strategic (Long-term)
												</h5>
												<div className="space-y-2">
													{insightsAnalysis.recommendations
														.filter((rec: any) => rec.priority === "low")
														.slice(0, 3)
														.map((rec: any, idx: number) => (
															<div
																key={idx}
																className="p-2 bg-blue-50 rounded text-sm"
															>
																<div className="font-medium">{rec.action}</div>
																<div className="text-xs text-muted-foreground mt-1">
																	{rec.reasoning}
																</div>
																<div className="text-xs text-blue-600 mt-1">
																	Timeframe: {rec.timeframe}
																</div>
															</div>
														))}
												</div>
											</div>
										)}
									</div>

									{insightsAnalysis.recommendations.length === 0 && (
										<div className="text-center py-6 text-muted-foreground">
											<Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
											<p className="text-sm">
												No recommendations available. Generate insights analysis
												to see priority interventions.
											</p>
										</div>
									)}
								</div>
							)}
					</div>

					{/* Behavioral Insights & Patterns (moved to bottom) */}
					<div className="space-y-6">
						{/* Signal Pattern Analysis */}
						{insightsAnalysis?.signalPatterns?.length > 0 && (
							<div className="p-4 border rounded-lg">
								<h4 className="font-medium mb-4 flex items-center">
									<Activity className="w-4 h-4 mr-2" />
									Signal Pattern Analysis
								</h4>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{insightsAnalysis.signalPatterns
										.slice(0, 4)
										.map((pattern: any, idx: number) => (
											<div key={idx} className="border rounded p-3">
												<div className="flex justify-between items-center mb-2">
													<h5 className="font-medium text-sm">
														{pattern.signal}
													</h5>
													<Badge variant="outline">
														{pattern.frequency}/10
													</Badge>
												</div>
												<p className="text-xs text-muted-foreground mb-1">
													{pattern.correlation}
												</p>
												<p className="text-xs text-muted-foreground">
													{pattern.impact}
												</p>
											</div>
										))}
								</div>
							</div>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
